// Herkunft (First Touch) des Besuchers fuer die Sales-Attribution (plugins/salesmart).
//
// Beim ersten Aufruf der App werden gclid, utm_* sowie Landingpage und Referrer aus
// der URL gelesen und im localStorage gemerkt. Die Website (www) dekoriert ihre
// Links in die App mit denselben Parametern plus `lp` (Landingpage auf www) und
// `ref` (Referrer auf www), damit der eigentliche Erstkontakt nicht verloren geht.
// createUser bekommt die Werte beim Anlegen des Accounts; updateUtmParams traegt
// sie fuer bestehende Sessions nach (Server fuellt nur leere Spalten).
//
// First Touch gewinnt: ein gespeicherter Eintrag wird nur ersetzt, wenn er keinen
// Kampagnenbezug (gclid/utm) hat und der neue einen hat.

export interface Attribution {
  gclid?: string;
  utmParams?: string;
  landingPage?: string;
  referrer?: string;
  capturedAt: string;
}

export type AttributionVariables = Pick<Attribution, 'gclid' | 'utmParams' | 'landingPage' | 'referrer'>;

const STORAGE_KEY = 'dieter.attribution';
const SYNCED_KEY = 'dieter.attribution.synced';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Parameter, die nie in landingPage landen duerfen (Tokens, Session-IDs, PII)
const STRIP_FROM_LANDING_PAGE = ['authtoken', 'token', 'session_id', 'email', 'password', 'ucc'];
const MAX = { gclid: 200, utmParams: 1000, landingPage: 2000, referrer: 1000 };

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function truncate(value: string | null | undefined, max: number): string | undefined {
  const v = value?.trim();
  return v ? v.slice(0, max) : undefined;
}

export function hasCampaignTouch(a: Pick<Attribution, 'gclid' | 'utmParams'> | null | undefined): boolean {
  return Boolean(a && (a.gclid || a.utmParams));
}

export function readStoredAttribution(): Attribution | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Attribution;
    if (!parsed?.capturedAt || Date.now() - new Date(parsed.capturedAt).getTime() > TTL_MS) {
      s.removeItem(STORAGE_KEY);
      s.removeItem(SYNCED_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function cleanLandingPage(href: string): string | undefined {
  try {
    const url = new URL(href);
    for (const key of STRIP_FROM_LANDING_PAGE) url.searchParams.delete(key);
    return truncate(url.toString(), MAX.landingPage);
  } catch {
    return truncate(href, MAX.landingPage);
  }
}

// Liest die Herkunft aus der aktuellen URL (und dem Referrer), ohne zu speichern.
export function attributionFromLocation(
  href: string,
  documentReferrer: string
): Omit<Attribution, 'capturedAt'> | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const q = url.searchParams;
  const gclid = truncate(q.get('gclid') || q.get('gbraid') || q.get('wbraid'), MAX.gclid);

  const utm = new URLSearchParams();
  for (const key of UTM_KEYS) {
    const v = q.get(key);
    if (v && v.trim()) utm.set(key, v.trim());
  }
  const utmParams = truncate(utm.toString(), MAX.utmParams);

  // `lp`/`ref` kommen von der Link-Dekoration der Website und beschreiben den
  // Erstkontakt auf www; sonst zaehlt die erste App-URL.
  const landingPage = truncate(q.get('lp'), MAX.landingPage) || cleanLandingPage(href);
  let referrer = truncate(q.get('ref'), MAX.referrer);
  if (!referrer && documentReferrer) {
    try {
      if (new URL(documentReferrer).host !== url.host) referrer = truncate(documentReferrer, MAX.referrer);
    } catch {
      /* ignore */
    }
  }

  const out: Omit<Attribution, 'capturedAt'> = {};
  if (gclid) out.gclid = gclid;
  if (utmParams) out.utmParams = utmParams;
  if (landingPage) out.landingPage = landingPage;
  if (referrer) out.referrer = referrer;
  return Object.keys(out).length ? out : null;
}

// Beim App-Start aufrufen. Gibt die danach gueltige Herkunft zurueck.
export function captureAttribution(
  href: string = typeof window !== 'undefined' ? window.location.href : '',
  documentReferrer: string = typeof document !== 'undefined' ? document.referrer : ''
): Attribution | null {
  const stored = readStoredAttribution();
  const current = href ? attributionFromLocation(href, documentReferrer) : null;
  if (!current) return stored;
  if (stored && (hasCampaignTouch(stored) || !hasCampaignTouch(current))) return stored;

  const next: Attribution = { ...current, capturedAt: new Date().toISOString() };
  const s = storage();
  try {
    s?.setItem(STORAGE_KEY, JSON.stringify(next));
    s?.removeItem(SYNCED_KEY);
  } catch {
    /* Storage voll oder gesperrt: Herkunft gilt trotzdem fuer diesen Aufruf */
  }
  return next;
}

// Variablen fuer createUser / updateUtmParams
export function attributionVariables(): AttributionVariables {
  const a = readStoredAttribution();
  if (!a) return {};
  const out: AttributionVariables = {};
  if (a.gclid) out.gclid = a.gclid;
  if (a.utmParams) out.utmParams = a.utmParams;
  if (a.landingPage) out.landingPage = a.landingPage;
  if (a.referrer) out.referrer = a.referrer;
  return out;
}

export function isAttributionSynced(userId: string): boolean {
  try {
    return storage()?.getItem(SYNCED_KEY) === userId;
  } catch {
    return false;
  }
}

export function markAttributionSynced(userId: string): void {
  try {
    storage()?.setItem(SYNCED_KEY, userId);
  } catch {
    /* ignore */
  }
}
